<template>
  <div :key="forceReRenderKey" class="pipeline-state-viewer border-radius p-3">
    <popup
        v-if="showDeletePrompt"
        @close="() => {showDeletePrompt = false;}"
    >
      <delete-prompt
          slot="popupMainContent"
          :custom-message="taskAbortMessage"
          @abort="() => {showDeletePrompt = false;}"
          @confirm="abortTask"
      />
    </popup>

    <!--<h3>Pipeline {{ $t('state') }}</h3>-->
    <div class="mb-2">
      <h4>{{ $t('processingState') }}</h4>
      <tag
          v-if="getPipelineState"
          :icon-left="null"
          :is-removable="false"
          :tag="getPipelineState"
          type="error"
      />
      <!-- STATE (E.G. YOUR TASK HAS BEEN ABORTED) --->
      <div v-if="!getPipelineState" :class="isAbortingOrAborted ? 'bg-highlight p-1' : ''">
        {{ $t(pipelineState) }}
      </div>
      <div v-if="isWaiting" class="w-100 form-error p-2 mt-2">
        {{ $t('notYetStarted') }}
      </div>
    </div>
    <div v-if="hasError" class="bg-highlight p-2 mb-3 w-100">
      {{ $t('pipelineErrorMsg') }}
    </div>

    <div v-if="hasTaskEntry">
      <!-- YOUR TASK IS BEING ABORTED -->
      <div
          v-if="(markAsAborting || isAborting)"
          class="bg-highlight p-2 w-100"
      >
        <loading-spinner class="white"/>
        {{ $t('abortingPipelineTask') }}
      </div>

      <!-- abort button -->
      <div
          v-if="isAbortableTaskType && !isAbortingOrAborted && !markAsAborting"
          class="settings-button d-inline-block mt-2"
          @click="showDeletePrompt = true">
        <icon class="mr-2" type="exclamation-triangle"/>
        {{ !hasError ? $t('abortPipelineTask') : $t('pipeline.deleteTaskAndCADFiles') }}
      </div>
    </div>

    <div v-if="showAbortedMessage && !hasTaskEntry" class="w-100">
      <div class="form-error mt-1">{{ importAbortedMessage }}</div>
      <div
          class="settings-button d-inline-block"
          @click="$emit('removeAbortedState')">
        {{ $t('abortedConfirm') }}
      </div>
    </div>

  </div>
</template>

<script>
/**
 * This component is used to display the state of a pipeline.
 * it expects a running task entry or will show "not running" if none is provided.
 *
 * **/
import DeletePrompt from "@/components/forms/DeletePrompt.vue";
import Popup from "@/components/Popup.vue";
import {AbortableTaskTypes, TaskManagerTaskColors, TaskManagerStates} from "@/enum";
import Icon from "@/components/Icon.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Task from "@/components/pipeline/task.js";
import {preImportSteps} from "@/enum";
import Tag from "@/components/Tag.vue";

export default {
  name: "PipelineStateViewer",
  components: {Popup, DeletePrompt, Icon, LoadingSpinner, Tag},
  props: {
    taskEntry: {type: Object, default: () => {}},
    objectId: {type: String, required: true},
    newestLogentry: {type: Object, default: () => {}},
    showAbortedMessage: {type: Boolean, default: false}
  },
  data() {
    return {
      showDeletePrompt: false,
      markAsAborting: false,
      preImportSteps,
      forceReRenderKey: 0
    }
  },
  computed: {
    /**
     * Strings generated:
     * pipeline.applyMaterialAbortedMessage
     * pipeline.convertHelperAbortedMessage
     * pipeline.convertAssemblyAbortedMessage
     * pipeline.instanceUpdatingAbortedMessage
     * pipeline.filterAbortedMessage
     * pipeline.extractAbortedMessage
     * pipeline.updateAbortedMessage
     * pipeline.dataprocessingAbortedMessage
     * */
    importAbortedMessage() {
      let task = '';
      if (this.newestLogentry) {
        task = this.newestLogentry.task;
        if (task) {
          const steps = Object.keys(preImportSteps).map(item => {
            return this.$t('pipeline.' + item);
          }).join(' | ');
          return this.$t('pipeline.' + task + 'AbortedMessage', {steps});
        }
      }
      return this.$t('pipeline.importAborted');
    },
    hasTaskEntry() {
      return !!this.taskEntry;
    },
    getPipelineState() {
      if (this.showAbortedMessage && !this.hasTaskEntry) {
        return {
          name: this.$t('pipeline.' + TaskManagerStates['778']),
          color: TaskManagerTaskColors['778']
        }
      }
      if (this.hasTaskEntry) {
        const obj = this.taskObject.getPipelineStateAndColor();
        if (obj && obj.name) {
          obj.name = this.$t('pipeline.' + obj.name);
        }
        return obj;
      }
      return null;
    },
    taskObject() {
      return new Task(this.taskEntry);
    },
    pipelineState() {
      if (this.taskObject) {
        const state = this.taskObject.getPipelineState();
        return this.$t(state);
      }
      return this.$t('noProcessRunning');
    },
    isAbortingOrAborted() {
      if (!this.taskEntry) {
        return false;
      }
      return this.taskObject.getIsAbortingOrAborted()
    },
    hasError() {
      if (!this.taskEntry) {
        return false;
      }
      return this.taskObject.hasError();
    },
    isWaiting() {
      if (!this.taskEntry) {
        return false;
      }
      return this.taskObject.isWaiting();
    },
    getTask() {
      return this.taskObject.getTaskName();
    },
    isAborted() {
      return this.taskObject.getIsAborted();
    },
    isAborting() {
      return this.taskObject.getIsAborting();
    },
    isAbortableTaskType() {
      const task = this.getTask;
      if (task) {
        if (Object.values(AbortableTaskTypes).includes(task)) {
          return true;
        }
      }
      return false;
    },
    taskAbortMessage() {
      /**
       * Translation-Strings:
       * pipeline.dataprocessingAbortMessage
       * pipeline.applyMaterialAbortMessage
       * pipeline.convertHelperAbortMessage
       * pipeline.convertAssemblyAbortMessage
       * pipeline.instanceUpdatingAbortMessage
       * pipeline.filterAbortMessage
       * pipeline.reimportAbortMessage
       * pipeline.notaskAbortMessage
       * pipeline.extractAbortMessage
       * pipeline.updateAbortMessage
       * */
      const task = this.getTask;
      if (task) {
        return this.$t('pipeline.' + task + 'AbortMessage');
      }
      return '';
    }
  },
  methods: {
    async abortTask() {
      this.showDeletePrompt = false;
      this.markAsAborting = true;
      await this.$store.dispatch('clientAbortPipelineTask', {
        id: this.objectId
      });
      this.$emit('abort');
    }
  }
}
</script>

<style lang="scss" scoped>
.pipeline-state-viewer {
  background-color: var(--vform-editor-ui-quinary-color);
}

.bg-darker {
  background-color: var(--vform-editor-ui-quinary-color);
}

.form-error {
  max-width: none;
}
</style>